const NEAR_DUPLICATE_WINDOW_MS = 60 * 1000;

function normalize(value) {
  if (value == null) return '';
  return String(value).trim();
}

export function getMessageText(message) {
  if (!message) return '';

  const raw =
    message?.content?.body?.text ||
    (typeof message?.content?.body === 'string' ? message.content.body : null) ||
    (typeof message?.content === 'string' ? message.content : null) ||
    message?.content?.text ||
    (typeof message?.body === 'string' ? message.body : null) ||
    message?.body?.text ||
    message?.text ||
    (typeof message?.message === 'string' ? message.message : null) ||
    '';

  return normalize(raw);
}

export function getMessageIdentityKeys(message) {
  if (!message) return [];

  return [
    message.id,
    message.msg_id,
    message.w_id,
    message.message_id,
  ]
    .filter((value) => value != null && value !== '')
    .map(String);
}

export function messagesShareIdentity(a, b) {
  const aKeys = new Set(getMessageIdentityKeys(a));
  const bKeys = getMessageIdentityKeys(b);

  return bKeys.some((key) => aKeys.has(key));
}

function getMessageTimestampMs(message) {
  if (!message) return null;

  const raw =
    message.created_at ||
    message.timestamp ||
    message.updated_at;

  if (raw == null) return null;

  if (typeof raw === 'number') {
    return raw < 10000000000 ? raw * 1000 : raw;
  }

  const parsed = Date.parse(raw);

  return Number.isFinite(parsed) ? parsed : null;
}

function getContentType(message) {
  return normalize(
    message?.content?.type ||
      message?.type ||
      ''
  ).toUpperCase();
}

function normalizeContentType(type) {
  const value = normalize(type).toUpperCase();

  if (value === 'FILE' || value === 'DOCUMENT') {
    return 'DOCUMENT';
  }

  if (value === 'IMAGE' || value === 'PHOTO') {
    return 'IMAGE';
  }

  if (value === 'VIDEO') {
    return 'VIDEO';
  }

  if (value === 'AUDIO' || value === 'VOICE') {
    return 'AUDIO';
  }

  if (value === 'TEXT') {
    return 'TEXT';
  }

  if (value === 'TEMPLATE') {
    return 'TEMPLATE';
  }

  return value;
}

function getTemplateName(message) {
  return normalize(
    message?.template_name ||
      message?.content?.template_name ||
      message?.content?.template?.name ||
      ''
  ).toLowerCase();
}

function getMediaLink(message) {
  return normalize(
    message?.content?.header?.link ||
      message?.content?.header?.document?.link ||
      message?.content?.header?.image?.link ||
      message?.content?.header?.video?.link ||
      message?.content?.header?.audio?.link ||
      message?.content?.document?.link ||
      message?.content?.document?.url ||
      message?.content?.document?.media_url ||
      message?.content?.file?.link ||
      message?.content?.file?.url ||
      message?.content?.file?.media_url ||
      message?.content?.media?.link ||
      message?.content?.media?.url ||
      message?.content?.image?.link ||
      message?.content?.image?.url ||
      message?.content?.video?.link ||
      message?.content?.video?.url ||
      message?.content?.audio?.link ||
      message?.content?.audio?.url ||
      message?.media?.link ||
      message?.media?.url ||
      message?.media_url ||
      message?.url ||
      ''
  );
}

function getMediaFilename(message) {
  return normalize(
    message?.content?.header?.filename ||
      message?.content?.header?.file_name ||
      message?.content?.header?.document?.filename ||
      message?.content?.header?.document?.file_name ||
      message?.content?.document?.filename ||
      message?.content?.document?.file_name ||
      message?.content?.document?.name ||
      message?.content?.file?.filename ||
      message?.content?.file?.file_name ||
      message?.content?.file?.name ||
      message?.content?.filename ||
      message?.content?.file_name ||
      message?.content?.name ||
      message?.filename ||
      message?.file_name ||
      message?.fileName ||
      ''
  ).toLowerCase();
}

function getMediaId(message) {
  return normalize(
    message?.content?.document?.id ||
      message?.content?.document?.media_id ||
      message?.content?.file?.id ||
      message?.content?.file?.media_id ||
      message?.content?.media?.id ||
      message?.content?.media?.media_id ||
      message?.media?.id ||
      message?.media?.media_id ||
      message?.media_id ||
      ''
  );
}

function getMimeType(message) {
  return normalize(
    message?.content?.document?.mime_type ||
      message?.content?.document?.mimetype ||
      message?.content?.file?.mime_type ||
      message?.content?.file?.mimetype ||
      message?.content?.mime_type ||
      message?.mime_type ||
      message?.mimetype ||
      ''
  ).toLowerCase();
}

function sameConversation(a, b) {
  const phoneA = normalize(a?.phone_number);
  const phoneB = normalize(b?.phone_number);

  const botA = normalize(a?.bot_number);
  const botB = normalize(b?.bot_number);

  if (phoneA && phoneB && phoneA !== phoneB) {
    return false;
  }

  if (botA && botB && botA !== botB) {
    return false;
  }

  return true;
}

function isSameContentType(a, b) {
  const typeA = normalizeContentType(getContentType(a));
  const typeB = normalizeContentType(getContentType(b));

  if (!typeA || !typeB) return true;

  return typeA === typeB;
}

function isSameMedia(a, b) {
  const idA = getMediaId(a);
  const idB = getMediaId(b);

  if (idA && idB && idA === idB) {
    return true;
  }

  const filenameA = getMediaFilename(a);
  const filenameB = getMediaFilename(b);

  if (filenameA && filenameB && filenameA === filenameB) {
    const mimeA = getMimeType(a);
    const mimeB = getMimeType(b);

    if (!mimeA || !mimeB || mimeA === mimeB) {
      return true;
    }
  }

  const linkA = getMediaLink(a);
  const linkB = getMediaLink(b);

  if (linkA && linkB) {
    if (linkA === linkB) {
      return true;
    }

    const normalizedA = linkA.split('?')[0];
    const normalizedB = linkB.split('?')[0];

    if (normalizedA === normalizedB) {
      return true;
    }
  }

  return false;
}

export function isNearDuplicateContent(a, b) {
  if (!a || !b) return false;

  if (normalize(a?.origin) !== normalize(b?.origin)) {
    return false;
  }

  if (!sameConversation(a, b)) {
    return false;
  }

  if (!isSameContentType(a, b)) {
    return false;
  }

  const typeA = normalizeContentType(getContentType(a));
  const typeB = normalizeContentType(getContentType(b));

  if (typeA === 'DOCUMENT' && typeB === 'DOCUMENT') {
    return isSameMedia(a, b);
  }

  const templateA = getTemplateName(a);
  const templateB = getTemplateName(b);

  if (templateA || templateB) {
    if (
      !templateA ||
      !templateB ||
      templateA !== templateB
    ) {
      return false;
    }

    const textA = getMessageText(a);
    const textB = getMessageText(b);
    const tsA = getMessageTimestampMs(a);
    const tsB = getMessageTimestampMs(b);

    if (textA && textB && textA === textB) {
      if (tsA == null || tsB == null) {
        return true;
      }
      return Math.abs(tsA - tsB) <= NEAR_DUPLICATE_WINDOW_MS;
    }

    if (isSameMedia(a, b)) {
      if (tsA == null || tsB == null) {
        return true;
      }
      return Math.abs(tsA - tsB) <= NEAR_DUPLICATE_WINDOW_MS;
    }

    return false;
  }

  const textA = getMessageText(a);
  const textB = getMessageText(b);

  if (textA && textB && textA === textB) {
    const tsA = getMessageTimestampMs(a);
    const tsB = getMessageTimestampMs(b);

    if (tsA == null || tsB == null) {
      return true;
    }

    return Math.abs(tsA - tsB) <= NEAR_DUPLICATE_WINDOW_MS;
  }

  if (isSameMedia(a, b)) {
    const tsA = getMessageTimestampMs(a);
    const tsB = getMessageTimestampMs(b);

    if (tsA == null || tsB == null) {
      return true;
    }

    return Math.abs(tsA - tsB) <= NEAR_DUPLICATE_WINDOW_MS;
  }

  return false;
}

export function isDuplicateInterveneMessage(existing, incoming) {
  if (!existing || !incoming) return false;

  if (messagesShareIdentity(existing, incoming)) {
    return true;
  }

  const existingIds = getMessageIdentityKeys(existing).filter((id) => !id.toLowerCase().startsWith('local-'));
  const incomingIds = getMessageIdentityKeys(incoming).filter((id) => !id.toLowerCase().startsWith('local-'));
  if (existingIds.length > 0 && incomingIds.length > 0) {
    return false;
  }

  return isNearDuplicateContent(existing, incoming);
}

function isOptimisticMessage(message) {
  return getMessageIdentityKeys(message).some((id) =>
    id.toLowerCase().startsWith('local-')
  );
}

export function findOptimisticOutboundIndex(messages, incoming) {
  if (!incoming) return -1;

  if (normalize(incoming?.origin).toUpperCase() !== 'OUTBOUND') {
    return -1;
  }

  const incomingType = normalizeContentType(
    getContentType(incoming)
  );

  const incomingText = getMessageText(incoming);
  const incomingTemplate = getTemplateName(incoming);

  return (messages || []).findIndex((message) => {
    if (!isOptimisticMessage(message)) {
      return false;
    }

    if (
      normalize(message?.origin).toUpperCase() !== 'OUTBOUND'
    ) {
      return false;
    }

    if (!sameConversation(message, incoming)) {
      return false;
    }

    const messageType = normalizeContentType(
      getContentType(message)
    );

    if (
      incomingType &&
      messageType &&
      incomingType !== messageType
    ) {
      return false;
    }

    if (
      incomingType === 'DOCUMENT' &&
      messageType === 'DOCUMENT'
    ) {
      return isSameMedia(message, incoming);
    }

    const messageTemplate = getTemplateName(message);

    if (incomingTemplate || messageTemplate) {
      if (
        !incomingTemplate ||
        !messageTemplate ||
        incomingTemplate !== messageTemplate
      ) {
        return false;
      }

      const messageText = getMessageText(message);

      if (
        incomingText &&
        messageText &&
        incomingText === messageText
      ) {
        return true;
      }

      return isSameMedia(message, incoming);
    }

    if (isSameMedia(message, incoming)) {
      return true;
    }

    const messageText = getMessageText(message);

    if (
      incomingText &&
      messageText &&
      incomingText === messageText
    ) {
      return true;
    }

    return false;
  });
}

export function mergeIncomingWithOptimistic(existing, incoming) {
  const msgId =
    incoming?.msg_id ||
    incoming?.w_id ||
    incoming?.id ||
    incoming?.message_id;

  const wId =
    incoming?.w_id ||
    incoming?.msg_id ||
    incoming?.id ||
    incoming?.message_id;

  return {
    ...existing,
    ...incoming,
    msg_id: msgId || existing?.msg_id,
    w_id: wId || existing?.w_id,
  };
}
